<template>
	<div class="session-recorder ui-elevation-1 panel">
		<ui-select
			v-model="sourceType"
			class="session-recorder__source-type-select"
			label="Source"
			@select="setDefaultSourceType"
		>
			<ui-select-item
				v-for="sourceItem in sourceTypeList"
				:key="sourceItem.key"
				:disabled="sourceItem.key === 'webrtc' && !hasWebRtc"
				:label="sourceItem.label"
				:value="sourceItem.key"
			/>
		</ui-select>
		<ui-switch
			v-if="streaming && sourceType !== 'rtmp'"
			v-model="enableStreaming"
			:label="`${enableStreaming ? 'Stream on' : 'Stream off'}`"
			class="session-recorder__toggle-streaming"
			large
			tooltip="OFF=Record only / ON=Record and stream"
			@change="toggleStreaming"
		/>
		<div
			v-if="enableStreaming && sourceType !== 'rtmp'"
			class="session-recorder__stream"
		>
			<ui-select
				v-model="streamId"
				class="session-recorder__stream__select"
				label="Stream"
				@open="getStreamList"
			>
				<ui-select-item
					v-for="streamItem in streamList"
					:key="streamItem.stream_id"
					:label="streamItem.title"
					:value="streamItem.stream_id"
					@click.native="setDefaultStream(streamItem.stream_id)"
				/>
			</ui-select>
			<ui-menu
				icon="fal fa-ellipsis-v fa-2x"
				tooltip="Stream actions"
			>
				<ui-menu-item
					icon="fal fa-plus fa-fw"
					label="Create new"
					@click="createStream"
				/>
				<ui-menu-item
					:disabled="!streamId"
					icon="fal fa-edit fa-fw"
					label="Edit"
					@click="editStream"
				/>
				<ui-menu-item
					:disabled="!streamId || Boolean(sessionList.find((session) => session.stream_id === streamId))"
					icon="fal fa-trash fa-fw"
					label="Delete"
					@click="deleteStream"
				/>
				<ui-menu-item
					:disabled="!streamId"
					icon="fal fa-share fa-fw"
					label="Share"
					@click="shareStream"
				/>
			</ui-menu>
		</div>
		<rtmp-control
			v-if="sourceType === 'rtmp'"
			:recorder="recorder"
			class="session-recorder__rtmp"
		/>
		<sip-control
			v-if="sourceType === 'video-conference'"
			:contact-list="contactList"
			:pending-contacts="pendingContacts"
			:recorder="recorder"
			:session-list="sessionList"
			:stream-id="streamId"
			class="session-recorder__video-conference"
			@updatePendingContacts="$emit('update:pendingContacts', $event)"
		/>
		<skype-control
			v-if="sourceType === 'skype-for-business'"
			:recorder="recorder"
			class="session-recorder__skype-for-business"
		/>
		<web-rtc-control
			v-if="sourceType === 'webrtc'"
			:session-list="sessionList"
			:stream-id="streamId"
			:stream-list="streamList"
			:recorder="recorder"
			class="session-recorder__webrtc"
		/>
		<stream-create-dialog
			ref="streamCreateDialog"
			@close="enableStreaming = Boolean(streamId)"
			@create="createdStream"
		/>
		<stream-delete-dialog
			ref="streamDeleteDialog"
			@delete="deletedStream"
		/>
		<stream-edit-dialog
			ref="streamEditDialog"
			@update="getStreamList"
		/>
		<stream-share-dialog ref="streamShareDialog" />
	</div>
</template>

<script>
import RtmpControl from "control/Rtmp";
import SipControl from "control/Sip";
import SkypeControl from "control/Skype";
import StreamCreateDialog from "components/stream/CreateDialog";
import StreamDeleteDialog from "components/stream/DeleteDialog";
import StreamEditDialog from "components/stream/EditDialog";
import StreamShareDialog from "components/stream/ShareDialog";
import WebRtcControl from "control/WebRtc";
import UiMenu from "lib/components/ui/Menu";
import UiMenuItem from "lib/components/ui/MenuItem";
import UiSelect from "lib/components/ui/Select";
import UiSelectItem from "lib/components/ui/SelectItem";
import UiSwitch from "lib/components/ui/SwitchButton";
import alert from "lib/modules/Alert";
import http from "lib/modules/Http";
import { hasBrowserWebRtcSupport } from "lib/modules/Util";

import find from "lodash/find";
import maxBy from "lodash/maxBy";

export default {
	name: "SessionRecorder",
	components: {
		RtmpControl,
		SipControl,
		SkypeControl,
		StreamCreateDialog,
		StreamDeleteDialog,
		StreamEditDialog,
		StreamShareDialog,
		WebRtcControl,
		UiMenu,
		UiMenuItem,
		UiSelect,
		UiSelectItem,
		UiSwitch
	},
	props: {
		contactList: {
			type: Array,
			required: true
		},
		pendingContacts: {
			type: Object,
			required: true
		},
		recorder: {
			type: Object,
			required: true
		},
		sessionList: {
			type: Array,
			required: true
		},
		streaming: {
			type: Boolean,
			required: true
		}
	},
	data() {
		return {
			enableStreaming: this.streaming && Boolean(this.recorder.stream_id),
			hasWebRtc: hasBrowserWebRtcSupport(),
			sourceType: this.getDefaultSourceType(),
			sourceTypeList: [
				{ key: "video-conference", label: "Video conference" },
				{ key: "webrtc", label: "Connected camera" },
				{ key: "rtmp", label: "RTMP(S)" },
				...$rec.sfbEnabled ? [{ key: "skype-for-business", label: "Skype for Business" }] : []
			],
			streamId: this.recorder.stream_id,
			streamList: []
		};
	},
	mounted() {
		this.getStreamList();
	},
	methods: {
		// stream
		createStream() {
			this.$refs.streamCreateDialog.open(this.recorder.recorder_id);
		},
		createdStream(stream) {
			this.getStreamList();

			if (stream.default)
				this.streamId = stream.stream_id;

			if (!stream.uri)
				this.shareStream(stream);
		},
		deleteStream(stream = find(this.streamList, ["stream_id", this.streamId])) {
			if (stream)
				this.$refs.streamDeleteDialog.open(stream);
		},
		deletedStream() {
			this.streamId = null;
			this.getStreamList();
		},
		editStream(stream = find(this.streamList, ["stream_id", this.streamId])) {
			if (stream)
				this.$refs.streamEditDialog.open(stream);
		},
		shareStream(stream = find(this.streamList, ["stream_id", this.streamId])) {
			if (stream)
				this.$refs.streamShareDialog.open(stream);
		},
		getStreamList() {
			http.get(`/api/v2/recorder/${this.recorder.recorder_id}/streamlist`)
			.then((response) => {
				this.streamList = response.data;

				if (this.streamId !== null && !this.streamList.find((stream) => stream.stream_id === this.streamId))
					this.streamId = null;

				if (this.streamList.length === 0)
					this.enableStreaming = false;

				if (!this.enableStreaming || this.streamId)
					return;

				// set stream with latest timestamp as default
				const lastUpdatedStreamItem = maxBy(this.streamList, (item) => item.updated || item.created);

				this.streamId = typeof lastUpdatedStreamItem === "undefined" ? null : lastUpdatedStreamItem.stream_id;
				this.setDefaultStream(this.streamId);
			})
			.catch((error) => {
				alert.error("Unable to get stream list", error.response);
			});
		},
		toggleStreaming(val) {
			if (val) {
				if (this.streamList.length === 0)
					this.createStream();
				else
					this.getStreamList();
			}
			else {
				// only way to turn streaming off, even if this.stream is not guaranteed to be default,
				// coule be changed inside another browers instance
				// see http://redmine.mns.vc/issues/394#note-16, last point under src/js/main2/components/session/Recorder.vue
				this.setDefaultStream(this.streamId);
				this.streamId = null;
			}
		},
		setDefaultStream(streamId) {
			if (streamId === null)
				return;

			http.patch(`/api/v2/recorder/stream/${streamId}`, { default: this.enableStreaming })
			.catch((error) => {
				alert.error("Unable to set default stream", error.response);
			});
		},
		// source type
		getDefaultSourceType() {
			const sourceTypeId = localStorage.getItem("user.conference_mode");

			if (sourceTypeId === "webrtc" && !hasBrowserWebRtcSupport())
				return "video-conference";

			return sourceTypeId || "video-conference";
		},
		setDefaultSourceType(sourceTypeId) {
			localStorage.setItem("user.conference_mode", sourceTypeId);
		}
	}
};
</script>

<style lang="scss" scoped>
.session-recorder {
	display: grid;
	grid-gap: 0 16px;
	grid-template-columns: minmax(auto, 677px) 148px 260px 215px; // scales up to 1440px, then right padded
	padding: 15px;
	@media (max-width: 983px) {
		grid-template-columns: auto 148px 260px;
	}
	@media (max-width: 767px) {
		grid-template-columns: auto 148px;
		.session-recorder__stream {
			grid-column: span 2;
		}
	}
	@media (max-width: 482px) {
		grid-template-columns: auto;
		.session-recorder__stream {
			grid-column: auto;
		}
	}
	@media (max-width: 322px) {
		// fixes a UI glitch on iPhone 5 size display (320x568)
		// when toggeling the stream on/off switch
		.session-recorder__stream__select {
			/deep/ .md-input {
				width: 100%;
			}
		}
	}
	.session-recorder__source-type-select {
		margin: 0 0 12px 0;
	}
	.session-recorder__toggle-streaming {
		align-self: center;
		margin: 0;
		/deep/ .ui-switch {
			margin: 0;
		}
	}
	.session-recorder__stream {
		display: flex;
		overflow: auto;
		.session-recorder__stream__select {
			margin: 0 0 12px 0;
			min-width: 190px;
		}
		.ui-menu {
			align-self: center;
			margin: 0;
		}
	}
	.session-recorder__rtmp,
	.session-recorder__video-conference,
	.session-recorder__skype-for-business,
	.session-recorder__webrtc {
		grid-column: span 4;
		@media (max-width: 983px) {
			grid-column: span 3;
		}
		@media (max-width: 767px) {
			grid-column: span 2;
		}
		@media (max-width: 482px) {
			grid-column: auto;
		}
	}
}

// IE11 specific, using flex due to poor grid support
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
	.session-recorder {
		display: flex;
		flex-flow: row wrap;
		min-width: 260px;
		.session-recorder__source-type-select {
			flex-basis: 282px;
			flex-grow: 1;
			@media (min-width: 768px) {
				flex-basis: 206px;
			}
		}
		.session-recorder__toggle-streaming {
			width: 148px;
			margin: 0 16px;
		}
		.session-recorder__stream {
			flex-basis: 282px;
			flex-grow: 1;
			@media (min-width: 768px) {
				max-width: 260px;
			}
			@media (min-width: 984px) {
				margin-right: 231px;
			}
		}
		.session-recorder__rtmp,
		.session-recorder__video-conference,
		.session-recorder__skype-for-business {
			flex-basis: 100%;
		}
	}
}
</style>
